"use client"

import type { Locale } from "@i18n/config"
import { setLocaleCookie } from "@lib/data/locale-actions"
import { useLocaleDirection } from "@lib/hooks/use-locale-direction"
import { ChevronDown, XMark } from "@medusajs/icons"
import { Button, RadioGroup, clx } from "@medusajs/ui"
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react"
import { useLocale, useTranslations } from "next-intl"
import { usePathname, useRouter } from "next/navigation"
import { useEffect, useState, useTransition } from "react"

/**
 * 语言切换（2026-10-31，下线老站之后补回）。
 *
 * 老站的「地区 + 语言」抽屉随 Medusa 一起删掉了，而语言切换本身仍然是刚需：
 * `_medusa_locale` 决定界面文案、`<html lang>`、以及定价卡的默认币种。
 * 这里只保留语言这一半 —— 国家/地区那一半随价格表落幕（新站的价格是
 * 「计划 × 周期 × 币种」，币种默认跟语言走，客人想换币种在结算页点币种 pill）。
 *
 * 保留老抽屉的 testid（`nav-region-select-button`、`close-region-select-button`、
 * `#region-language-{code}`、`apply-region-button`）与「应用 / Apply」按钮文案：
 * e2e-ego 的 74-locale-switch 与 41-mobile-layout 都按这些标识定位。
 */
const LocaleSelect = ({
  locales,
  className,
}: {
  locales: Locale[]
  className?: string
}) => {
  const t = useTranslations("RegionSelect")
  const tLang = useTranslations("LanguageSelect")
  const currentLocale = useLocale()
  const pathname = usePathname()
  const router = useRouter()
  const dir = useLocaleDirection()
  const [open, setOpen] = useState(false)
  const [pendingLocale, setPendingLocale] = useState(currentLocale)
  const [isPending, startTransition] = useTransition()

  useEffect(() => {
    if (!open) return
    setPendingLocale(currentLocale)
  }, [open, currentLocale])

  function handleApply() {
    if (pendingLocale === currentLocale) {
      setOpen(false)
      return
    }

    const segments = pathname.split("/").filter(Boolean)
    segments[0] = pendingLocale
    const newPath = "/" + segments.join("/")

    startTransition(async () => {
      // cookie 先写：`router.push` 之后服务端要按新 cookie 渲染，晚一步就会出现
      // 「URL 已经是 /en、文案还是中文」的半截状态（74 的 cookie 断言也依赖它）。
      await setLocaleCookie(pendingLocale)
      setOpen(false)
      router.push(newPath)
    })
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="nav-region-select-button"
        className={clx(
          // 36px 是「zh + 箭头」的天然宽度，作为触摸目标偏窄（41-mobile-layout
          // 的门槛是 40）：补一点内边距把可点区域撑到 50 上下，视觉上仍贴着右边。
          "flex items-center gap-x-1.5 rounded-rounded px-2 py-1.5 text-ui-fg-subtle hover:text-ui-fg-base transition-colors",
          className
        )}
      >
        <span className="uppercase">{currentLocale}</span>
        <ChevronDown className="shrink-0 w-2.5 h-2.5" />
      </button>

      <Transition show={open}>
        <Dialog onClose={() => setOpen(false)} className="relative z-[65]">
          <TransitionChild
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25" aria-hidden="true" />
          </TransitionChild>

          <div className="fixed inset-0 flex justify-end">
            <TransitionChild
              enter="ease-out duration-250"
              enterFrom="opacity-0 translate-x-8"
              enterTo="opacity-100 translate-x-0"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-x-0"
              leaveTo="opacity-0 translate-x-8"
            >
              <DialogPanel className="flex flex-col h-[calc(100%-16px)] w-[calc(100%-16px)] sm:w-1/3 2xl:w-1/4 sm:min-w-[360px] m-2 rounded-rounded bg-ui-bg-base p-6 gap-6">
                <div className="flex items-center justify-between shrink-0">
                  <DialogTitle className="txt-xlarge text-ui-fg-base">
                    {t("tabLanguage")}
                  </DialogTitle>
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    aria-label={t("close")}
                    data-testid="close-region-select-button"
                    className="text-ui-fg-subtle hover:text-ui-fg-base transition-colors p-0.5"
                  >
                    <XMark />
                  </button>
                </div>

                <div className="flex flex-1 min-h-0 overflow-x-hidden overflow-y-auto no-scrollbar">
                  <RadioGroup
                    dir={dir}
                    value={pendingLocale}
                    onValueChange={setPendingLocale}
                    disabled={isPending}
                    className="gap-1"
                  >
                    {locales.map((item) => (
                      <label
                        key={item.code}
                        htmlFor={`region-language-${item.code}`}
                        className="flex items-center gap-x-3 rounded-lg px-3 py-2 cursor-pointer hover:bg-ui-bg-base-hover transition-colors min-w-0"
                      >
                        <RadioGroup.Item
                          value={item.code}
                          id={`region-language-${item.code}`}
                        />
                        <span className="txt-compact-small text-ui-fg-base truncate">
                          {tLang(`locales.${item.code}`, {
                            fallback: item.name,
                          })}
                        </span>
                      </label>
                    ))}
                  </RadioGroup>
                </div>

                <Button
                  type="button"
                  size="large"
                  data-testid="apply-region-button"
                  className="w-full shrink-0"
                  disabled={isPending}
                  isLoading={isPending}
                  onClick={handleApply}
                >
                  {t("apply")}
                </Button>
              </DialogPanel>
            </TransitionChild>
          </div>
        </Dialog>
      </Transition>
    </>
  )
}

export default LocaleSelect
